import React, { useState } from 'react';
import { Input, Select, Avatar } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';

import { MasterConfigs } from '../../../../../../interfaces/entity';
import ReusableTable from '../../../../../../components/TableComponent';

const { Option } = Select;

interface DeploymentRecord {
  _id: string;
  developer: {
    name: string;
    code: string;
  };
  jobTitle: string;
  dealName: string;
  assignedDate: string;
  client: string;
  email: string;
  contactNumber: string;
  engagementTime: string;
  budget: string;
  billingType: string;
  leadType: string;
  stage: 'Won' | 'In Discussion' | 'Dropped';
}

const DeveloperDeploymentTable: React.FC = () => {
  const [search, setSearch] = useState('');
  const [dealType, setDealType] = useState<string | undefined>(undefined);

  const tableConfig: MasterConfigs = {
    api: () => '/developer-deployment-history',
    loadPagination: true,
    queryParams: { 
      search,
      dealType 
    },

    renderComponent: () => (
      <div className="flex justify-between items-center mb-4 px-1">
        <h3 className="text-base font-semibold text-gray-800">Developer Deployment History</h3>
        <div className="flex gap-3">
          <Input
            size="middle"
            placeholder="Search"
            prefix={<SearchOutlined className="text-gray-400" />}
            className="w-64 rounded-md border-gray-300"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            placeholder="Select deal type"
            className="w-40"
            allowClear
            onChange={(value) => setDealType(value)}
          >
            <Option value="contract">Contract</Option>
            <Option value="fix_bid">Fix Bid</Option>
          </Select>
        </div>
      </div>
    ),

    getColumns: (): ColumnsType<DeploymentRecord> => [
      {
        title: 'Developer',
        dataIndex: 'developer',
        // Removed fixed: 'left' to match previous scroll behavior
        render: (dev) => (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <Avatar size="small" className="bg-gray-200 text-gray-600 text-[10px] font-bold">
              {dev.name.charAt(0)}
            </Avatar>
            <div className="flex flex-col">
              <span className="text-xs font-bold text-gray-700 leading-tight">{dev.name}</span>
              <span className="text-[10px] text-gray-400 font-semibold">{dev.code}</span>
            </div>
          </div>
        ),
      },
      {
        title: 'Job Title',
        dataIndex: 'jobTitle',
        render: (text) => <span className="text-xs text-gray-600 whitespace-nowrap">{text}</span>,
      },
      {
        title: 'Deals Name',
        dataIndex: 'dealName',
        render: (text) => <span className="text-xs text-gray-600 whitespace-nowrap">{text}</span>,
      },
      {
        title: 'Assigned Date',
        dataIndex: 'assignedDate',
        render: (text) => <span className="text-xs text-gray-600 whitespace-nowrap">{text}</span>,
      },
      {
        title: 'Client',
        dataIndex: 'client',
        render: (text) => <span className="text-xs text-gray-600 whitespace-nowrap">{text}</span>,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        render: (text) => <span className="text-xs text-gray-600 whitespace-nowrap">{text}</span>,
      },
      {
        title: 'Contact Number',
        dataIndex: 'contactNumber',
        render: (text) => <span className="text-xs text-gray-600 whitespace-nowrap">{text}</span>,
      },
      {
        title: 'Engagement Time',
        dataIndex: 'engagementTime',
        render: (text) => <span className="text-xs text-gray-600 whitespace-nowrap">{text}</span>,
      },
      {
        title: 'Budget',
        dataIndex: 'budget',
        render: (text) => <span className="text-xs text-gray-600 whitespace-nowrap">{text || '-'}</span>,
      },
      {
        title: 'Billing Type',
        dataIndex: 'billingType',
        render: (text) => <span className="text-xs text-gray-600 whitespace-nowrap">{text || '-'}</span>,
      },
      {
        title: 'Lead type',
        dataIndex: 'leadType',
        render: (text) => <span className="text-xs text-gray-600 whitespace-nowrap">{text}</span>,
      },
      {
        title: 'Stage',
        dataIndex: 'stage',
        render: (stage) => (
          <span className="text-xs text-gray-600 font-medium whitespace-nowrap">
            {stage}
          </span>
        ),
      },
      {
        title: 'Timeline',
        align: 'center',
        // Removed fixed: 'right'
        render: () => (
          <span className="text-blue-500 cursor-pointer hover:underline text-xs font-medium whitespace-nowrap">
            View
          </span>
        ),
      },
    ],

    tableProps: {
      bordered: false,
      size: 'middle', // Adjusted to middle to match the spacing of previous component
      scroll: { x: 'max-content' }, 
      pagination: {
        size: 'small',
        showSizeChanger: false,
      }
    },
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 mt-6 pb-[50px]">
      <ReusableTable<DeploymentRecord> config={tableConfig} />
    </div>
  );
};

export default DeveloperDeploymentTable;